import { useCallback, memo } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
  Chip,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
} from "@mui/material";
import {
  Add as AddIcon,
  DeleteOutline as DeleteIcon,
  GroupAdd as GroupAddIcon,
} from "@mui/icons-material";
import FilterRule from "./FilterRule";
import { type FilterGroupData, type FilterRuleRow, type ItsColumnDef } from "./constants";

type FilterGroupProps = {
  group: FilterGroupData;
  onUpdate: (group: FilterGroupData) => void;
  onDelete: () => void;
  level?: number;
  availableColumns: ItsColumnDef[];
};

const FilterGroup = memo(
  ({
    group,
    onUpdate,
    onDelete,
    level = 0,
    availableColumns: _availableColumns,
  }: FilterGroupProps) => {
    const columns = _availableColumns;

    const addRule = useCallback(() => {
      const newRule: FilterRuleRow = {
        id: `rule_${Date.now()}_${Math.random()}`,
        field: columns[0]?.id || "ITS_ID",
        operator: "equals",
        value: "",
      };
      onUpdate({
        ...group,
        rules: [...(group.rules || []), newRule],
      });
    }, [columns, group, onUpdate]);

    const addNestedGroup = useCallback(() => {
      const newGroup: FilterGroupData = {
        id: `group_${Date.now()}_${Math.random()}`,
        logic: "AND",
        rules: [
          {
            id: `rule_${Date.now()}_${Math.random()}`,
            field: columns[0]?.id || "ITS_ID",
            operator: "equals",
            value: "",
          },
        ],
        groups: [],
      };
      onUpdate({
        ...group,
        groups: [...(group.groups || []), newGroup],
      });
    }, [columns, group, onUpdate]);

    const updateRule = useCallback(
      (ruleId: string, updatedRule: FilterRuleRow) => {
        onUpdate({
          ...group,
          rules: (group.rules || []).map((r) => (r.id === ruleId ? updatedRule : r)),
        });
      },
      [group, onUpdate]
    );

    const deleteRule = useCallback(
      (ruleId: string) => {
        onUpdate({
          ...group,
          rules: (group.rules || []).filter((r) => r.id !== ruleId),
        });
      },
      [group, onUpdate]
    );

    const updateNestedGroup = useCallback(
      (groupId: string, updatedGroup: FilterGroupData) => {
        onUpdate({
          ...group,
          groups: (group.groups || []).map((g) => (g.id === groupId ? updatedGroup : g)),
        });
      },
      [group, onUpdate]
    );

    const deleteNestedGroup = useCallback(
      (groupId: string) => {
        onUpdate({
          ...group,
          groups: (group.groups || []).filter((g) => g.id !== groupId),
        });
      },
      [group, onUpdate]
    );

    const handleLogicChange = useCallback(
      (newLogic: "AND" | "OR") => {
        onUpdate({
          ...group,
          logic: newLogic,
        });
      },
      [group, onUpdate]
    );

    const isRoot = level === 0;

    return (
      <Paper
        elevation={0}
        sx={{
          p: { xs: 1.5, sm: 2 },
          mb: 1.5,
          border: 1,
          borderColor: isRoot ? "divider" : "primary.light",
          borderLeft: isRoot ? undefined : 3,
          borderLeftColor: isRoot ? undefined : "primary.main",
          borderRadius: 2,
          bgcolor: isRoot ? "background.paper" : "action.hover",
          position: "relative",
        }}
      >
        {/* Group Header: Logic Control & Meta */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1.25,
            mb: 1.5,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}>
            <ToggleButtonGroup
              size="small"
              value={group.logic}
              exclusive
              onChange={(_, val) => {
                if (val) handleLogicChange(val as "AND" | "OR");
              }}
              sx={{
                height: 28,
                bgcolor: "background.paper",
                "& .MuiToggleButton-root": {
                  px: 1.25,
                  py: 0.25,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "none",
                  "&.Mui-selected": {
                    bgcolor: group.logic === "AND" ? "primary.main" : "secondary.main",
                    color: "#fff",
                    "&:hover": {
                      bgcolor: group.logic === "AND" ? "primary.dark" : "secondary.dark",
                    },
                  },
                },
              }}
            >
              <ToggleButton value="AND">Match ALL (AND)</ToggleButton>
              <ToggleButton value="OR">Match ANY (OR)</ToggleButton>
            </ToggleButtonGroup>

            <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
              {group.logic === "AND"
                ? "Recipients must satisfy all conditions"
                : "Recipients can satisfy any condition"}
            </Typography>
          </Box>

          {!isRoot && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Chip
                label={`Nested Group (Level ${level})`}
                size="small"
                variant="outlined"
                sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
              />
              <Tooltip title="Delete this group">
                <IconButton
                  color="error"
                  size="small"
                  onClick={onDelete}
                  sx={{ width: 28, height: 28 }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </Box>
          )}
        </Box>

        {/* Rules Stack */}
        {group.rules && group.rules.length > 0 ? (
          <Stack spacing={1} sx={{ mb: 1.5 }}>
            {group.rules.map((rule) => (
              <FilterRule
                key={rule.id}
                rule={rule}
                onUpdate={(updated) => updateRule(rule.id, updated)}
                onDelete={() => deleteRule(rule.id)}
                availableColumns={columns}
              />
            ))}
          </Stack>
        ) : (
          <Box
            sx={{
              p: 2,
              mb: 1.5,
              borderRadius: 1.5,
              border: "1px dashed",
              borderColor: "divider",
              textAlign: "center",
              bgcolor: "background.paper",
            }}
          >
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: "0.82rem" }}>
              No conditions in this group yet. Click <strong>Add Condition</strong> below to start.
            </Typography>
          </Box>
        )}

        {/* Nested Groups */}
        {group.groups && group.groups.length > 0 && (
          <Stack spacing={1} sx={{ mb: 1.5, ml: { xs: 0, sm: 1.5 } }}>
            {group.groups.map((nestedGroup) => (
              <FilterGroup
                key={nestedGroup.id}
                group={nestedGroup}
                onUpdate={(updated) => updateNestedGroup(nestedGroup.id, updated)}
                onDelete={() => deleteNestedGroup(nestedGroup.id)}
                level={level + 1}
                availableColumns={columns}
              />
            ))}
          </Stack>
        )}

        {/* Action Buttons */}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
          <Button
            variant="outlined"
            size="small"
            startIcon={<AddIcon />}
            onClick={addRule}
            sx={{ fontSize: "0.8rem", textTransform: "none", fontWeight: 600 }}
          >
            Add Condition
          </Button>
          <Button
            variant="outlined"
            size="small"
            color="secondary"
            startIcon={<GroupAddIcon />}
            onClick={addNestedGroup}
            sx={{ fontSize: "0.8rem", textTransform: "none", fontWeight: 600 }}
          >
            Add Group
          </Button>
        </Stack>
      </Paper>
    );
  }
);

FilterGroup.displayName = "FilterGroup";

export default FilterGroup;
